import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type {} from '@redux-devtools/extension';

import {
  Date,
  ExtendedQRCodeParams,
  QRTypes,
  StoreSetterFunctionType,
} from '@type';
import { getDateNow } from '@utils';

const STORE_NAME: string = 'USER_REGISTER';

type RegisterInfo = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  birthdate: Date;
  profilePicture: string | null;
  patientSourceUUID: string;
  siteUUID?: string;
  qrData?: ExtendedQRCodeParams;
};

type RegisterState = {
  registerInfo: RegisterInfo;
  progress?: number;
};

const initialState: RegisterState = {
  registerInfo: {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    birthdate: getDateNow(),
    profilePicture: null,
    patientSourceUUID: '',
  },
  progress: undefined,
};

type RegisterActions = {
  updateRegisterInfo: (
    info: Partial<RegisterInfo>,
    isDeepLink?: boolean
  ) => void;
  resetRegisterStore: () => void;
};

export type RegisterGetType = () => RegisterState & RegisterActions;

export type RegisterSetType = StoreSetterFunctionType<
  RegisterState,
  RegisterActions
>;

const updateRegisterInfo =
  (set: RegisterSetType, get: RegisterGetType) =>
  (updatedInfo: Partial<RegisterInfo>, isDeepLink?: boolean) => {
    const currentInfo = get().registerInfo;
    const registerInfo = {
      ...currentInfo,
      ...updatedInfo,
      qrData: currentInfo.qrData,
    };

    if (updatedInfo.qrData)
      registerInfo.qrData = {
        ...updatedInfo.qrData,
        isClassicOnboarding: updatedInfo.qrData?.type === QRTypes.Classic,
        isDeepLink: updatedInfo.qrData?.isDeepLink ?? isDeepLink,
      } as ExtendedQRCodeParams;

    set({ registerInfo }, false, `${STORE_NAME}-SET_REGISTER_INFO`);
  };

const resetRegisterStore = (set: RegisterSetType) => () =>
  set(initialState, false, `${STORE_NAME}-RESET`);

export const useRegisterStore = create<RegisterState & RegisterActions>()(
  devtools(
    (set, get) => ({
      ...initialState,
      updateRegisterInfo: updateRegisterInfo(set, get),
      resetRegisterStore: resetRegisterStore(set),
    }),
    {
      name: STORE_NAME,
    }
  )
);
